import { z } from 'zod';
import { handoffReasonSchema, leadContactTypeSchema } from '../../conversations.js';
import { usageSchema } from './billing.js';
/**
 * One website's analytics over a period (features/website-analytics): how
 * many people chatted and how many became leads, how the assistant answered,
 * what visitors ask most and what the knowledge doesn't cover, live chat, and
 * when visitors chat. Needs the analytics permission.
 *
 * Definitions (features/website-analytics/decisions.md):
 * - A conversation is in the period when it started in it.
 * - An engaged conversation has at least one visitor message.
 * - Every visitor message is a question. An uncovered question is one the
 *   knowledge had no answer for (handoff reason `no_answer`).
 * - Questions are grouped when their wording is the same, ignoring case,
 *   punctuation and extra spaces; a group shows its most common wording.
 * - Messages per conversation counts every message but the greeting, over
 *   engaged conversations.
 */
export const ANALYTICS_PERIODS = [7, 30, 90];
export const DEFAULT_ANALYTICS_PERIOD = 30;
export const analyticsQuerySchema = z.object({
    days: z.coerce
        .number()
        .int()
        .refine((value) => ANALYTICS_PERIODS.includes(value), {
        message: 'Choose 7, 30 or 90 days.',
    })
        .default(DEFAULT_ANALYTICS_PERIOD),
    /** The viewer's IANA time zone, for days and hours; UTC when not given. */
    tz: z.string().max(64).optional(),
});
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const analyticsTotalsSchema = z.object({
    conversations: z.number().int(),
    engagedConversations: z.number().int(),
    leads: z.number().int(),
    /** Visitor messages. */
    questions: z.number().int(),
    /** Questions the assistant answered from the knowledge. */
    answered: z.number().int(),
    /** Questions handed off for any reason but no answer in the knowledge. */
    handedOff: z.number().int(),
    /** Questions the knowledge had no answer for. */
    uncovered: z.number().int(),
    /** Questions the assistant never answered because a person took the chat over. */
    byAgent: z.number().int(),
    /** Every message but the greeting, per engaged conversation; `null` with none. */
    messagesPerConversation: z.number().nullable(),
});
export const analyticsDaySchema = z.object({
    date: isoDate,
    conversations: z.number().int(),
    engagedConversations: z.number().int(),
    leads: z.number().int(),
});
export const questionGroupSchema = z.object({
    /** The wording asked most often (the latest of those on a tie). */
    question: z.string(),
    count: z.number().int(),
    answered: z.number().int(),
    handedOff: z.number().int(),
    uncovered: z.number().int(),
    lastAskedAt: z.iso.datetime(),
    /** The conversation it was last asked in, to read it, and its website. */
    conversationId: z.uuid(),
    websiteId: z.uuid(),
    websiteName: z.string(),
});
export const agentActivitySchema = z.object({
    name: z.string(),
    chatsTakenOver: z.number().int(),
    replies: z.number().int(),
    transfersOut: z.number().int(),
});
/** What both the website and the account analytics hold. */
const analyticsSchema = z.object({
    period: z.object({ from: isoDate, to: isoDate, days: z.number().int(), timeZone: z.string() }),
    totals: analyticsTotalsSchema,
    /** The same figures for the period just before, for the change. */
    previous: analyticsTotalsSchema,
    leadsByType: z.array(z.object({ contactType: leadContactTypeSchema, count: z.number().int() })),
    /** Every day of the period, oldest first, including days with nothing. */
    daily: z.array(analyticsDaySchema),
    /** Why the assistant handed off, most frequent first (uncovered included as `no_answer`). */
    handoffReasons: z.array(z.object({ reason: handoffReasonSchema, count: z.number().int() })),
    /** Up to 20 groups, most asked first. */
    topQuestions: z.array(questionGroupSchema),
    /** Up to 20 groups of uncovered questions, most asked first. */
    uncoveredQuestions: z.array(questionGroupSchema),
    liveChat: z.object({
        takenOver: z.number().int(),
        /** From the visitor's first message to the takeover; `null` with none. */
        medianSecondsToTakeover: z.number().nullable(),
        /** From the takeover to the first agent reply; `null` with none. */
        medianSecondsToFirstReply: z.number().nullable(),
        agents: z.array(agentActivitySchema),
    }),
    /** Conversations started, by weekday (0 Sunday) and hour, in the period's time zone. */
    busyHours: z.array(z.object({
        weekday: z.number().int().min(0).max(6),
        hour: z.number().int().min(0).max(23),
        conversations: z.number().int(),
    })),
});
export const websiteAnalyticsSchema = analyticsSchema.extend({
    /** This billing cycle's AI-answered messages, against the plan. */
    usage: usageSchema,
});
/** One website's figures in the account overview. */
export const websiteSummarySchema = z.object({
    websiteId: z.uuid(),
    name: z.string(),
    conversations: z.number().int(),
    engagedConversations: z.number().int(),
    leads: z.number().int(),
    questions: z.number().int(),
    answered: z.number().int(),
    uncovered: z.number().int(),
    takenOver: z.number().int(),
});
/**
 * The account overview: the same figures across all the account's websites
 * (deleted ones left out), and each website's own, most conversations first.
 */
export const accountAnalyticsSchema = analyticsSchema.extend({
    websites: z.array(websiteSummarySchema),
});
/**
 * Every website in the account together, for the Overview. Needs the
 * analytics permission, which covers every website in the account.
 */
export const getAccountAnalytics = {
    method: 'GET',
    path: '/api/dashboard/analytics',
    params: z.object({}),
    query: analyticsQuerySchema,
    body: z.object({}),
    response: accountAnalyticsSchema,
};
export const getWebsiteAnalytics = {
    method: 'GET',
    path: '/api/dashboard/websites/:websiteId/analytics',
    params: z.object({ websiteId: z.uuid() }),
    query: analyticsQuerySchema,
    body: z.object({}),
    response: websiteAnalyticsSchema,
};
//# sourceMappingURL=analytics.js.map